/* CASH REPORT — what one entity's version holds, drawn the same way wherever it is read.

   Max, 2026-10-08: the templates are imported in their own module (Cash Report Import, one tab per
   template) and the three reports are read where they always were. Both screens show the version
   that is in the flow — the Preparer to check what was just imported, the Reviewer, the Group CFO
   and the CEO to decide on it — so the lines are drawn by ONE component per report, here, and the
   two screens cannot come to print one version two ways. */
import React from 'react';
import { CheckCircle2, FileText } from 'lucide-react';
import { moneyIn, Num } from './cashUi';
import { Field1, Field2Head, SetupTick, FileCell, TopTable } from './CashT1';
import { SheetTable, SheetTitle, monthLabel } from './CashPlanSheet';
import { MonthEndHead } from './CashForms';

// The account as Field 2 draws it: the id, and under it the bank and the number on the statement.
const AcctCell = ({ l }) => (
  <><span className="mono">{l.account_id}</span>{(l.bank_name || l.account_no) && <div className="cw-sub">{[l.bank_name, l.account_no].filter(Boolean).join(' · ')}</div>}</>
);

/* ---- the weekly report's own lines, inside the shared panel ----------------------------------
   Template 1 as Tuan's sheet lays it out (CashT1.jsx), read-only: what the Reviewer, the Group CFO
   and the CEO decide on is the same Fields the Preparer filled in — balances only since 2026-10-29. */
export function WeeklyBody({ t, d, filesFor, fileLink }) {
  const cur = d.current;
  // Field 1 only once every account has a rate — see Field1.
  const totals = cur.lines.length && cur.lines.every((l) => l.sgd) ? cur.totals : null;
  return (
    <>
      <Field1 t={t} totals={totals} />

      <h4 className="cw-h">{t('cashf.field2')}</h4>
      <div className="cs-table-wrap">
        <table className="pa-table cw-table t1-f2">
          <Field2Head t={t} />
          <tbody>
            {cur.lines.map((l) => {
              const bad = cur.checks.some((c) => c.level === 'error' && (c.accounts?.length ? c.accounts : [c.account]).includes(l.account_id));
              return (
                <tr key={l.account_id} className={bad ? 'cw-bad' : ''} data-account={l.account_id}>
                  <td><AcctCell l={l} /></td>
                  <td>{l.currency}</td>
                  <td className="num" data-col="closing"><Num v={l.closing_stmt} ccy={l.currency} /></td>
                  <td className="num">{l.restricted_amt ? moneyIn(l.restricted_amt, l.currency) : ''}</td>
                  <td className="num">{l.fd_amt ? moneyIn(l.fd_amt, l.currency) : ''}</td>
                  <td className="num"><Num v={l.available_amt ?? null} ccy={l.currency} /></td>
                  <td className="num" title={l.rate_source ? t(`cashw.rateFrom_${l.rate_source}`) : t('cashf.rateMissing', { ccy: l.currency })}>
                    {l.rate != null ? l.rate : <span className="cw-down">—</span>}
                  </td>
                  <td className="cf-center"><SetupTick t={t} on={l.restricted} label={t('xCash.aria.restricted', { id: l.account_id })} /></td>
                  <td className="cf-center"><SetupTick t={t} on={l.fixed_deposit && !l.restricted} label={t('xCash.aria.fixedDeposit', { id: l.account_id })} /></td>
                  <FileCell t={t} kind="statement" account={l.account_id} filesFor={filesFor} fileLink={fileLink} />
                  <FileCell t={t} kind="balance" account={l.account_id} filesFor={filesFor} fileLink={fileLink} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {['spend', 'payable'].map((kind) => (
        <TopTable key={kind} t={t} kind={kind} catName={(x) => x.category}
          rows={[1, 2, 3].map((rank) => cur.tops.find((x) => x.kind === kind && Number(x.rank_no) === rank) || { rank_no: rank })} />
      ))}
    </>
  );
}

/* ---- the Monthly Cash Plan's own figures (2026-10-25) ----------------------------------------
   The entity's own sheet, as the group cash forecast lays it out (CashPlanSheet.jsx) — what the Preparer checks after an
   import and what the Reviewer agrees to. */
export function PlanBody({ t, d }) {
  const c = d.current;
  if (!c?.sheet) return null;
  const entity = d.record?.entity_code || '';
  return (
    <>
      <div className="cw-totals">
        <div><span>{t('xCashMp.r_begin')} · {monthLabel(c.months[0])}</span><b><Num v={c.rows[0]?.begin} ccy={c.currency} /></b></div>
        <div><span>{t('xCashMp.r_end')} · {monthLabel(c.months[c.months.length - 1])}</span><b><Num v={c.rows[c.rows.length - 1]?.end} ccy={c.currency} /></b></div>
      </div>
      <SheetTitle entity={entity} format={c.format} />
      {c.format.isDefault && <p className="cs-hint">{t('xCashMp.standardFormat', { entity })}</p>}
      <SheetTable t={t} format={c.format} rows={c.sheet} months={c.months} />
    </>
  );
}

/* ---- the month-end reconcile's own lines ----------------------------------------------------- */
export function ReconBody({ t, d, filesFor, fileLink }) {
  const cur = d.current;
  return (
    <>
      <h4 className="cw-h">{t('cashm.accountsTitle')}</h4>
      <div className="cs-table-wrap">
        <table className="pa-table cw-table">
          <MonthEndHead t={t} week={cur.rule.week || ''}
            tail={<><th rowSpan={2} className="num">{t('cashm.h_reconcile')}</th><th rowSpan={2}>{t('cashf.h_upStatement')}</th></>} />
          <tbody>
            {cur.lines.map((l) => {
              const bad = cur.checks.some((c) => c.level === 'error' && (c.accounts?.length ? c.accounts : [c.account]).includes(l.account_id));
              return (
                <tr key={l.account_id} className={bad ? 'cw-bad' : ''}>
                  <td><AcctCell l={l} /></td>
                  <td>{l.currency}</td>
                  <td className="num"><Num v={l.month_end_closing} ccy={l.currency} /></td><td className="num"><Num v={l.weekend_closing} ccy={l.currency} /></td>
                  <td className="num"><Num v={l.variance} ccy={l.currency} /></td><td className="num"><Num v={l.net_inflow} ccy={l.currency} /></td>
                  <td className="num"><Num v={l.net_outflow} ccy={l.currency} /></td>
                  <td className="num">{l.reconcile === 0 ? <CheckCircle2 size={13} className="cw-ok" /> : (l.reconcile != null ? <span className="cw-down">{moneyIn(l.reconcile, l.currency)}</span> : '')}</td>
                  <td>
                    {filesFor(l.account_id).map((f) => (
                      <a key={f.file_pk} className="cw-file" href={fileLink(f)} target="_blank" rel="noreferrer" title={f.original_name}>
                        <FileText size={12} /> {t(`cashw.kind_${f.kind}`)}
                      </a>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="cw-totals">
        <div><span>{t('cashm.reconciled')}</span><b>{cur.totals.reconciled} / {cur.totals.accounts}</b></div>
        <div><span>{t('cashm.totalSgd')}</span><b><Num v={cur.totals.month_end_sgd} ccy="SGD" /></b></div>
      </div>
    </>
  );
}
